<template>
  <MainLayout>
    <template v-slot:header>
      <Header
        :showBackButton="
          currentState === localConstants.VIEW_STATES.FILTERS ||
            currentState === localConstants.VIEW_STATES.DETAIL
        "
        :showRefreshButton="showRefreshButton"
        @handleBack="handleBack"
        @handleRefresh="handleRefresh"
      />
    </template>
    <template v-slot:content>
      <FilterAlert
        :visible="showFilterAlert"
        @toggle="showFilterAlert = false"
      />
      <v-slide-x-reverse-transition hide-on-leave>
        <Filters
          class="mb-10 pb-10"
          v-show="currentState === localConstants.VIEW_STATES.FILTERS"
          :selectedFilters="selectedFilters"
          :typeOptions="typeOptions"
          @change="handleFiltersChange"
        />
      </v-slide-x-reverse-transition>
      <v-slide-x-transition hide-on-leave>
        <div>
          <div
            v-show="currentState === localConstants.VIEW_STATES.LIST"
            class="px-4 pt-4 pb-15 mb-6"
          >
            <div class="d-flex justify-center align-center mb-4">
              <Search
                class="pe-2"
                :disabled="hasNoRevs"
                :terms="terms"
                @search="handleSearch"
                @clearSearch="clearSearch"
              />
              <Button
                plain
                color="primary"
                icon="mdi-filter-variant"
                :buttonOpacity="1"
                @click="goToFilterView"
              />
            </div>
            <template v-if="isLoading">
              <RevCardSkeleton v-for="item in ITEMS_PER_PAGE" :key="item" />
            </template>

            <template v-else>
              <template v-if="showFilterEmpty">
                <EmptyStateFilters
                  style="height: 70vh"
                  :title="title"
                  :message="message"
                />
              </template>
              <Card
                v-for="rev in revs"
                :key="rev.id"
                :isImporting="rev.id === importRevId"
                :rev="rev"
                @select="handleSelectCard(rev.id)"
                @import="handleImportFromCard(rev.id)"
              />
            </template>
          </div>
          <div v-show="currentState === localConstants.VIEW_STATES.EMPTY">
            <EmptyState style="height: 70vh" />
          </div>
        </div>
      </v-slide-x-transition>
      <v-slide-x-reverse-transition hide-on-leave>
        <div
          v-show="currentState === localConstants.VIEW_STATES.DETAIL"
          class="px-4 pt-4 pb-15 mb-6"
        >
          <RevDetailView :rev="selectedRev" />
        </div>
      </v-slide-x-reverse-transition>
    </template>
    <template v-slot:footer>
      <div style="width: 100%">
        <v-divider />
        <div class="px-6 py-4" v-if="showPagination">
          <v-pagination
            circle
            total-visible="5"
            :length="totalPages"
            :value="currentPage"
            @input="handlePageChange"
          />
        </div>
        <div
          class="px-6 py-4"
          v-if="currentState === localConstants.VIEW_STATES.FILTERS"
        >
          <Button
            block
            :text="$t('common.apply')"
            @click="handleApplyFilters"
          />
        </div>
        <div
          v-if="currentState === localConstants.VIEW_STATES.DETAIL"
          class="d-flex justify-space-between ma-0 pa-4"
          style="width: 100%"
        >
          <Button
            outlined
            size="small"
            icon="mdi-open-in-new"
            color="primary"
            :text="$t('common.go_to_rev')"
            @click="goToRev"
          />
          <Button
            size="small"
            icon="mdi-tray-arrow-down"
            color="primary"
            :text="$t('cards.import')"
            @click="handleImportFromDetail"
          />
        </div>
      </div>
    </template>
  </MainLayout>
</template>

<script>
import api from '@/api'
import office from '@/office/index.js'
import store from '@/store/index.js'
import constants from '@/constants'
import user from '@/mixins/user.vue'
import rev from '@/mixins/rev.vue'
import localConstants from '@/ui/powerpoint/views/constants.js'
import parseExploreRev from '@/ui/powerpoint/views/parseExploreRev.js'
import buildImportStructure from '../buildImportStrcuture'

import _ from 'lodash'

import MainLayout from '@/ui/shared/components/Layouts/MainLayout.vue'
import Header from '@/ui/shared/components/Header.vue'
import Search from '@/ui/powerpoint/components/Search.vue'
import EmptyState from '@/ui/powerpoint/components/EmptyState.vue'
import EmptyStateFilters from '@/ui/powerpoint/components/EmptyStateFilters.vue'
import Card from '@/ui/powerpoint/components/Card.vue'
import Button from '@/ui/shared/components/Button.vue'
import RevCardSkeleton from '@/ui/powerpoint/components/RevCardSkeleton.vue'
import Filters from '@/ui/shared/components/Filters/Filters.vue'
import FilterAlert from '@/ui/shared/components/FilterAlert.vue'
import RevDetailView from '../RevDetailView/RevDetailView.vue'

const ITEMS_PER_PAGE = 15
export default {
  name: 'HomeView',

  mixins: [rev, user],

  components: {
    Search,
    EmptyState,
    Card,
    Button,
    RevCardSkeleton,
    MainLayout,
    Header,
    Filters,
    FilterAlert,
    RevDetailView,
    EmptyStateFilters,
  },

  data() {
    return {
      buildImportStructure,
      parseExploreRev,
      constants,
      localConstants,
      revs: [],
      templateSelected: 'default',
      terms: '',
      isLoading: true,
      currentPage: 1,
      totalPages: 0,
      totalRecordsInData: 0,
      ITEMS_PER_PAGE,
      currentState: localConstants.VIEW_STATES.LIST,
      selectedFilters: _.cloneDeep({
        ...localConstants.DEFAULT_POWER_POINT_FILTERS,
      }),
      showFilterAlert: false,
      importRevId: '',
      selectedRev: {},
      error: null,
      filtersChanged: false,
      showFilterEmpty: false,
    }
  },

  mounted() {
    this.selectedFilters.sites = [
      this.currentUser?.site?._id ?? this.currentUser?.site,
    ]
    this.fetchRevs()
  },

  computed: {
    currentUser() {
      return store.access.getAccount()
    },

    organization() {
      return store.organization.getOrganization()
    },

    organizationId() {
      return store.access.getOrganizationId()
    },

    loading() {
      return this.loading || this.isImporting
    },

    hasSearchResults() {
      return Boolean(this.searchResults?.length > 0)
    },

    hasNoRevs() {
      return this.ifHasSearchResults && !this.terms
    },

    message() {
      if (this.isLoading) {
        return ''
      }

      if (this.error) {
        return ''
      }

      return this.terms
        ? this.$t('search.not_found_message')
        : this.$t('common.empty_state_message')
    },

    title() {
      if (this.error) {
        return this.$t('excel.check_your_connection_error')
      }

      return this.terms
        ? this.$t('search.not_found_title', { term: this.terms })
        : this.$t('excel.no_results_found')
    },

    showPagination() {
      return (
        this.totalPages > 1 &&
        this.totalRecordsInData > ITEMS_PER_PAGE &&
        this.currentState === this.localConstants.VIEW_STATES.LIST
      )
    },

    typeOptions() {
      return _.compact([
        this.organization?.featuresEnabled__improvementIdea && {
          id: 'improvementIdea',
          text:
            this.organization?.customNamings?.improvementIdea?.singular ||
            this.$t('common.rev_type.improvement_idea'),
        },
        this.organization?.featuresEnabled__quickAction && {
          id: 'quickAction',
          text:
            this.organization?.customNamings?.quickAction?.singular ||
            this.$t('common.rev_type.quick_action'),
        },
        this.organization?.featuresEnabled__problemSolving && {
          id: 'problemSolving',
          text:
            this.organization?.customNamings?.problemSolving?.singular ||
            this.$t('common.rev_type.problem_solving'),
        },
      ])
    },

    showBackButton() {
      return (
        this.currentState === localConstants.VIEW_STATES.FILTERS ||
        this.currentState === localConstants.VIEW_STATES.DETAIL
      )
    },

    showRefreshButton() {
      return (
        this.currentState === localConstants.VIEW_STATES.LIST ||
        this.currentState === localConstants.VIEW_STATES.EMPTY ||
        !this.filtersChanged
      )
    },
  },

  methods: {
    goToFilterView() {
      this.$track('msOffice.filterButton', {
        env: window.Office.context.host,
      })
      this.currentState = this.localConstants.VIEW_STATES.FILTERS
    },

    async handleImport(revId) {
      this.importRevId = revId
      const selectedRev = this.revs.find(rev => rev.id === revId)
      const revCustomNaming = this.typeOptions.find(
        type => type.id === selectedRev.entityType
      )?.text
      const rev = this.parseExploreRev(selectedRev)
      const importStructure = this.buildImportStructure({
        ...rev,
        revType: revCustomNaming,
      })
      await this.toPowerpoint(importStructure)
      setTimeout(() => (this.importRevId = ''), 200)
    },

    handleImportFromCard(revId) {
      this.$track('msOffice.import', {
        env: window.Office.context.host,
        screen: 'powerPointCard',
        revId: revId,
      })
      this.handleImport(revId)
    },

    async handleImportFromDetail() {
      this.$track('msOffice.import', {
        env: window.Office.context.host,
        screen: 'powerPointRevDetails',
        revId: this.selectedRev?.id,
      })
      await this.handleImport(this.selectedRev?.id)
      this.currentState = this.localConstants.VIEW_STATES.LIST
    },

    async toPowerpoint(data) {
      return await office.powerpoint.add(data)
    },

    handlePageChange(page) {
      if (this.currentPage === page) return
      this.currentPage = page
      this.fetchRevs()
    },

    async fetchRevs() {
      try {
        this.showFilterEmpty = false
        this.isLoading = true
        const { data } = await api.stream.getExplore(this.organizationId, {
          from: (this.currentPage - 1) * ITEMS_PER_PAGE,
          limit: ITEMS_PER_PAGE,
          terms: this.terms,
          sort: {
            by: 'updatedAt',
            order: 'desc',
          },
          filters: this.selectedFilters,
          projections: this.localConstants.POWER_POINT_PROJECTIONS,
          expansions: this.localConstants.POWER_POINT_EXPANSIONS,
        })

        this.revs = data?.results.map(result => result.response) ?? []

        this.totalRecordsInData = data?.totalRecordsInData
        this.totalPages =
          Math.ceil(this.totalRecordsInData / ITEMS_PER_PAGE) || 0

        if (data?.totalRecordsInData === 0 && this.filtersChanged) {
          return (this.showFilterEmpty = true)
        }
        if (data?.totalRecordsInData === 0) {
          return (this.currentState = this.localConstants.VIEW_STATES.EMPTY)
        }
      } catch (err) {
        this.error = err.message
      } finally {
        this.isLoading = false
      }
    },

    clearSearch() {
      this.terms = ''
      return this.fetchRevs()
    },

    handleBack() {
      this.$track('msOffice.backButton', {
        env: window.Office.context.host,
        screen: this.currentState,
      })

      this.currentState = this.localConstants.VIEW_STATES.LIST
    },

    handleFiltersChange(values, filter) {
      this.filtersChanged = true
      this.selectedFilters[filter] = values
    },

    handleApplyFilters() {
      if (_.isEmpty(this.selectedFilters?.types ?? [])) {
        return (this.showFilterAlert = true)
      }
      this.$track('msOffice.filter', {
        env: window.Office.context.host,
        selectedFilters: this.selectedFilters,
      })
      this.currentState = this.localConstants.VIEW_STATES.LIST
      this.fetchRevs()
    },

    handleSelectCard(revId) {
      this.$track('msOffice.revDetails', {
        env: window.Office.context.host,
        revId: revId,
      })
      const selectedRev = this.revs.find(rev => rev.id === revId)
      this.selectedRev = this.parseExploreRev(selectedRev)

      this.$nextTick()
      this.currentState = this.localConstants.VIEW_STATES.DETAIL
      window.scrollTo(0, 0)
    },

    kebabCaseType(entityType) {
      return entityType.replace(/[A-Z]/g, m => '-' + m.toLowerCase())
    },

    goToRev() {
      const url = this.getRevLink(this.selectedRev)

      this.$track('msOffice.redirect', {
        env: window.Office.context.host,
        url,
      })

      window.Office.context.ui.openBrowserWindow(url)
    },

    handleSearch(terms) {
      this.$track('msOffice.searchBar', {
        env: window.Office.context.host,
      })
      this.terms = terms
      this.fetchRevs()
    },

    handleRefresh() {
      this.fetchRevs()
    },
  },
}
</script>
